
import { Target, Search, Shield, Eye, AlertTriangle, Activity } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const ThreatHunting = () => {
  return (
    <LandingPageTemplate
      title="Threat Hunting Services"
      description="Proactive threat hunting services to identify and eliminate hidden threats before they can harm your organization."
      keywords={[
        "threat hunting",
        "advanced persistent threats",
        "threat detection",
        "cyber threat intelligence",
        "security monitoring",
        "IOC detection"
      ]}
      canonicalUrl="/cybersecurity/threat-hunting/"
      schemaType="service"
      schemaData={{
        name: "Threat Hunting Services",
        description: "Proactive detection of hidden threats in your environment",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Proactive"
        highlighted="Threat Hunting"
        description="Don't wait for alerts. Our expert threat hunters actively search for hidden threats in your environment, uncovering sophisticated attackers before they can cause damage."
        ctaText="Start Hunting"
        heroPattern="grid"
      />

      <FeatureSection 
        title="Advanced Threat Hunting"
        subtitle="CAPABILITIES"
        description="Our threat hunting services leverage expert analysts and advanced technologies to uncover sophisticated threats."
        features={[
          {
            title: "Active Threat Searching",
            description: "Proactively search for hidden threats in your environment.",
            icon: <Target className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Behavioral Analysis",
            description: "Detect unusual patterns that indicate attacker presence.",
            icon: <Activity className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Threat Intelligence",
            description: "Leverage current intelligence on attacker TTPs.",
            icon: <Eye className="h-6 w-6 text-purple-600" />
          },
          {
            title: "IOC Detection",
            description: "Identify indicators of compromise across systems.",
            icon: <Search className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Advanced Analytics",
            description: "AI-powered analysis of security telemetry.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Security Monitoring",
            description: "Continuous monitoring for malicious activity.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Threat Hunting Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to uncovering hidden threats"
        steps={[
          {
            title: "Intelligence Gathering",
            description: "Collect threat intelligence to inform hunting activities"
          },
          {
            title: "Hypothesis Formation",
            description: "Develop targeted hunting hypotheses based on threat intelligence"
          },
          {
            title: "Investigation Techniques",
            description: "Deploy specialized techniques to search for threat activity"
          },
          {
            title: "Threat Validation",
            description: "Validate and analyze potential threats discovered"
          },
          {
            title: "Threat Elimination",
            description: "Contain and eliminate confirmed threats"
          }
        ]}
      />

      <BenefitSection
        title="Benefits of Proactive Threat Hunting"
        benefits={[
          {
            title: "Early Threat Detection",
            description: "Detect threats before they trigger alerts"
          },
          {
            title: "Reduced Dwell Time",
            description: "Minimize attacker time in your environment"
          },
          {
            title: "Complete Threat Visibility",
            description: "See beyond what automated tools detect"
          },
          {
            title: "Advanced Threat Intelligence",
            description: "Gain insights into emerging threats"
          },
          {
            title: "Improved Security Posture",
            description: "Strengthen defenses through findings"
          },
          {
            title: "Insider Threat Detection",
            description: "Identify internal risks before damage occurs"
          }
        ]}
      />

      <CtaSection 
        title="Ready to Hunt for Hidden Threats?"
        description="Schedule a consultation to discuss how our threat hunting services can uncover hidden risks in your environment."
        ctaText="Schedule a Consultation"
        background="purple"
        modalTitle="Request Threat Hunting Services"
        modalDescription="Tell us about your organization's security environment and concerns."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Threat Hunting",
          service_interest: "Security Services"
        }}
      />
    </LandingPageTemplate>
  );
};

export default ThreatHunting;
